import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Plus, Trash2, Edit3, Key, Copy, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "stats", label: "Vue d'ensemble" },
  { key: "products", label: "Produits" },
  { key: "orders", label: "Commandes" },
  { key: "quotes", label: "Devis B2B" },
  { key: "users", label: "Utilisateurs" },
  { key: "apikeys", label: "Clés API" },
];

export default function AdminDashboardPage() {
  const [tab, setTab] = useState("stats");

  return (
    <div data-testid="admin-page">
      <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-green mb-2">Administration</div>
      <h1 className="text-4xl font-black tracking-tighter text-white">Tableau de bord</h1>

      <nav className="mt-8 flex flex-wrap gap-2 border-b border-brand-blue/40">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`admin-tab-${t.key}`}
            className={`px-4 h-10 text-xs font-bold uppercase tracking-[0.18em] border-b-2 transition-colors ${
              tab === t.key
                ? "border-brand-green text-brand-green"
                : "border-transparent text-white/70 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="mt-8">
        {tab === "stats" && <StatsView />}
        {tab === "products" && <ProductsView />}
        {tab === "orders" && <OrdersView />}
        {tab === "quotes" && <QuotesView />}
        {tab === "users" && <UsersView />}
        {tab === "apikeys" && <ApiKeysView />}
      </div>
    </div>
  );
}

// ---------- Stats ----------
function StatsView() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)); }, []);
  if (!stats) return <Loader2 className="h-6 w-6 animate-spin text-brand-green" />;
  const cards = [
    { label: "Produits", value: stats.products },
    { label: "Commandes", value: stats.orders },
    { label: "Utilisateurs", value: stats.users },
    { label: "Devis B2B", value: stats.quotes },
    { label: "Chiffre d'affaires", value: `${stats.revenue.toFixed(0)} €` },
    { label: "Stock faible", value: stats.low_stock, alert: stats.low_stock > 0 },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="admin-stats">
      {cards.map((c) => (
        <div key={c.label} className={`border rounded-sm p-5 ${c.alert ? "border-red-500/60" : "border-brand-blue/40"}`}>
          <div className="text-[10px] uppercase tracking-[0.25em] text-brand-gray/70 font-bold">{c.label}</div>
          <div className={`mt-2 font-black text-3xl tracking-tight ${c.alert ? "text-red-400" : "text-white"}`}>{c.value}</div>
        </div>
      ))}
    </div>
  );
}

// ---------- Products ----------
const EMPTY_PRODUCT = {
  name: "", brand: "", category: "laptop", description: "",
  price: 0, original_price: 0, grade: "A", stock: 0, images: "",
  specs: "", featured: false,
};

function ProductsView() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/products"); setProducts(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Produit supprimé");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-extrabold text-white">Produits ({products.length})</h2>
        <button
          onClick={() => setEditing({ ...EMPTY_PRODUCT })}
          data-testid="admin-add-product-button"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-sm bg-brand-green text-black font-bold hover:bg-brand-green/90 text-sm"
        >
          <Plus className="h-4 w-4" /> Ajouter
        </button>
      </div>
      {loading ? (
        <Loader2 className="h-6 w-6 animate-spin text-brand-green" />
      ) : (
        <div className="border border-brand-blue/40 rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-products-table">
            <thead className="bg-brand-navy/40 text-[10px] uppercase tracking-wider text-brand-gray/80">
              <tr>
                <th className="text-left px-4 py-3">Nom</th>
                <th className="text-left px-4 py-3">Catégorie</th>
                <th className="text-left px-4 py-3">Grade</th>
                <th className="text-right px-4 py-3">Prix</th>
                <th className="text-right px-4 py-3">Stock</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-blue/30 font-roboto">
              {products.map((p) => (
                <tr key={p.id} data-testid={`admin-product-row-${p.id}`}>
                  <td className="px-4 py-3 text-white font-bold">{p.name}<div className="text-xs text-brand-gray/60 font-normal">{p.brand}</div></td>
                  <td className="px-4 py-3 text-brand-gray">{p.category}</td>
                  <td className="px-4 py-3"><span className="px-2 py-1 bg-brand-green/15 text-brand-green text-xs font-bold rounded-sm">{p.grade}</span></td>
                  <td className="px-4 py-3 text-right text-white font-bold">{p.price.toFixed(2)} €</td>
                  <td className={`px-4 py-3 text-right font-bold ${p.stock < 5 ? "text-red-400" : "text-white"}`}>{p.stock}</td>
                  <td className="px-4 py-3 flex justify-end gap-2">
                    <button onClick={() => setEditing(p)} data-testid={`admin-edit-${p.id}`} className="p-2 text-white/70 hover:text-white"><Edit3 className="h-4 w-4" /></button>
                    <button onClick={() => remove(p.id)} data-testid={`admin-delete-${p.id}`} className="p-2 text-white/70 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <ProductFormModal initial={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

function ProductFormModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial.id;
  const [form, setForm] = useState({
    ...initial,
    images: Array.isArray(initial.images) ? initial.images.join("\n") : (initial.images || ""),
    specs: typeof initial.specs === "object" ? JSON.stringify(initial.specs, null, 2) : (initial.specs || ""),
  });
  const [saving, setSaving] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let specs = {};
      if (form.specs?.trim()) {
        try { specs = JSON.parse(form.specs); }
        catch { toast.error("Specs JSON invalide"); setSaving(false); return; }
      }
      const payload = {
        name: form.name, brand: form.brand, category: form.category,
        description: form.description, price: parseFloat(form.price) || 0,
        original_price: form.original_price ? parseFloat(form.original_price) : null,
        grade: form.grade, stock: parseInt(form.stock) || 0,
        images: (form.images || "").split("\n").map((s) => s.trim()).filter(Boolean),
        specs, featured: !!form.featured,
      };
      if (isEdit) await api.put(`/products/${initial.id}`, payload);
      else await api.post("/products", payload);
      toast.success(isEdit ? "Produit mis à jour" : "Produit créé");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Erreur", { description: e.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form
        onSubmit={save}
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-product-form"
        className="w-full max-w-xl bg-black border border-brand-blue/60 rounded-sm p-6 my-10 space-y-3"
      >
        <h3 className="text-xl font-black text-white">{isEdit ? "Modifier le produit" : "Nouveau produit"}</h3>
        <div className="grid grid-cols-2 gap-3">
          <In label="Nom" v={form.name} onChange={update("name")} required testId="form-name" />
          <In label="Marque" v={form.brand} onChange={update("brand")} required testId="form-brand" />
          <Sel label="Catégorie" v={form.category} onChange={update("category")} options={[{v:"laptop",l:"Ordinateur"},{v:"tablet",l:"Tablette"},{v:"phone",l:"Téléphone"},{v:"monitor",l:"Écran"}]} testId="form-category" />
          <Sel label="Grade" v={form.grade} onChange={update("grade")} options={[{v:"A",l:"A"},{v:"B",l:"B"},{v:"C",l:"C"}]} testId="form-grade" />
          <In label="Prix €" type="number" step="0.01" v={form.price} onChange={update("price")} required testId="form-price" />
          <In label="Prix barré €" type="number" step="0.01" v={form.original_price} onChange={update("original_price")} testId="form-original-price" />
          <In label="Stock" type="number" v={form.stock} onChange={update("stock")} required testId="form-stock" />
          <label className="flex items-center gap-2 mt-6 text-sm">
            <input type="checkbox" checked={!!form.featured} onChange={update("featured")} data-testid="form-featured" /> Mis en avant
          </label>
        </div>
        <Ta label="Description" v={form.description} onChange={update("description")} rows={3} testId="form-description" />
        <Ta label="Images (1 URL par ligne)" v={form.images} onChange={update("images")} rows={2} testId="form-images" />
        <Ta label='Specs (JSON, ex: {"cpu":"M2"})' v={form.specs} onChange={update("specs")} rows={3} testId="form-specs" />
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-sm border border-brand-blue/60 text-white text-sm">Annuler</button>
          <button type="submit" disabled={saving} data-testid="form-save-button" className="h-10 px-5 rounded-sm bg-brand-green text-black font-bold text-sm">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </button>
        </div>
      </form>
    </div>
  );
}

function In({ label, v, onChange, testId, ...rest }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-brand-gray mb-1">{label}</span>
      <input value={v ?? ""} onChange={onChange} data-testid={testId} {...rest} className="w-full h-10 px-3 bg-brand-navy/40 border border-brand-blue/60 rounded-sm text-white text-sm" />
    </label>
  );
}
function Ta({ label, v, onChange, testId, ...rest }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-brand-gray mb-1">{label}</span>
      <textarea value={v ?? ""} onChange={onChange} data-testid={testId} {...rest} className="w-full px-3 py-2 bg-brand-navy/40 border border-brand-blue/60 rounded-sm text-white text-sm font-roboto" />
    </label>
  );
}
function Sel({ label, v, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-brand-gray mb-1">{label}</span>
      <select value={v} onChange={onChange} data-testid={testId} className="w-full h-10 px-3 bg-brand-navy/40 border border-brand-blue/60 rounded-sm text-white text-sm">
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}

// ---------- Orders ----------
function OrdersView() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/orders"); setOrders(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    let tracking_number = null, carrier = null;
    if (status === "shipped") {
      tracking_number = window.prompt("Numéro de suivi ?") || null;
      carrier = window.prompt("Transporteur ?") || null;
    }
    await api.put(`/admin/orders/${id}`, { status, tracking_number, carrier });
    toast.success("Commande mise à jour");
    load();
  };

  if (loading) return <Loader2 className="h-6 w-6 animate-spin text-brand-green" />;
  if (orders.length === 0) return <p className="text-brand-gray font-roboto">Aucune commande.</p>;
  return (
    <div className="border border-brand-blue/40 rounded-sm overflow-x-auto">
      <table className="w-full text-sm" data-testid="admin-orders-table">
        <thead className="bg-brand-navy/40 text-[10px] uppercase tracking-wider text-brand-gray/80">
          <tr>
            <th className="text-left px-4 py-3">N°</th>
            <th className="text-left px-4 py-3">Client</th>
            <th className="text-left px-4 py-3">Date</th>
            <th className="text-right px-4 py-3">Total</th>
            <th className="text-left px-4 py-3">Statut</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-brand-blue/30 font-roboto">
          {orders.map((o) => (
            <tr key={o.id}>
              <td className="px-4 py-3 text-white font-mono text-xs">#{o.id.slice(0, 8)}</td>
              <td className="px-4 py-3 text-white">{o.user_name}<div className="text-xs text-brand-gray/60">{o.user_email}</div></td>
              <td className="px-4 py-3 text-brand-gray text-xs">{new Date(o.created_at).toLocaleDateString("fr-FR")}</td>
              <td className="px-4 py-3 text-right font-bold text-white">{o.total.toFixed(2)} €</td>
              <td className="px-4 py-3"><span className="px-2 py-1 bg-brand-blue/30 text-white text-xs font-bold rounded-sm">{o.status}</span></td>
              <td className="px-4 py-3">
                <select onChange={(e) => updateStatus(o.id, e.target.value)} value={o.status} data-testid={`order-status-${o.id}`} className="h-8 px-2 bg-brand-navy/40 border border-brand-blue/60 rounded-sm text-white text-xs">
                  <option value="paid">Payée</option>
                  <option value="shipped">Expédiée</option>
                  <option value="delivered">Livrée</option>
                  <option value="cancelled">Annulée</option>
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Quotes ----------
function QuotesView() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/b2b/quotes").then(({ data }) => setQuotes(data)).finally(() => setLoading(false));
  }, []);
  if (loading) return <Loader2 className="h-6 w-6 animate-spin text-brand-green" />;
  if (quotes.length === 0) return <p className="text-brand-gray font-roboto">Aucun devis B2B.</p>;
  return (
    <div className="space-y-3" data-testid="admin-quotes">
      {quotes.map((q) => (
        <div key={q.id} className="border border-brand-blue/40 rounded-sm p-4">
          <div className="flex justify-between items-start gap-3">
            <div>
              <div className="font-bold text-white">{q.company} — {q.contact_name}</div>
              <div className="text-xs text-brand-gray/70 font-roboto">{q.email}{q.phone ? ` · ${q.phone}` : ""}</div>
            </div>
            <span className="text-[10px] uppercase tracking-[0.2em] text-brand-green font-bold">{q.status}</span>
          </div>
          <ul className="mt-3 list-disc list-inside text-sm text-brand-gray font-roboto">
            {q.items?.map((it, i) => <li key={i}>{it.description || JSON.stringify(it)}</li>)}
          </ul>
          {q.notes && <p className="mt-2 text-xs text-brand-gray font-roboto italic">"{q.notes}"</p>}
        </div>
      ))}
    </div>
  );
}

// ---------- Users ----------
function UsersView() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/users").then(({ data }) => setUsers(data)).finally(() => setLoading(false));
  }, []);
  if (loading) return <Loader2 className="h-6 w-6 animate-spin text-brand-green" />;
  return (
    <div className="border border-brand-blue/40 rounded-sm overflow-x-auto">
      <table className="w-full text-sm" data-testid="admin-users-table">
        <thead className="bg-brand-navy/40 text-[10px] uppercase tracking-wider text-brand-gray/80">
          <tr>
            <th className="text-left px-4 py-3">Nom</th>
            <th className="text-left px-4 py-3">E-mail</th>
            <th className="text-left px-4 py-3">Rôle</th>
            <th className="text-left px-4 py-3">Inscription</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-brand-blue/30 font-roboto">
          {users.map((u) => (
            <tr key={u.id}>
              <td className="px-4 py-3 text-white">{u.name}{u.company && <div className="text-xs text-brand-gray/60">{u.company}</div>}</td>
              <td className="px-4 py-3 text-brand-gray">{u.email}</td>
              <td className="px-4 py-3"><span className={`px-2 py-1 text-xs font-bold rounded-sm ${u.role === "admin" ? "bg-brand-green/20 text-brand-green" : "bg-brand-blue/30 text-white"}`}>{u.role}</span></td>
              <td className="px-4 py-3 text-brand-gray/70 text-xs">{new Date(u.created_at).toLocaleDateString("fr-FR")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- API Keys ----------
function ApiKeysView() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/api-keys"); setKeys(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    await api.post("/admin/api-keys", { name });
    toast.success("Clé créée");
    setName("");
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Révoquer cette clé ?")) return;
    await api.delete(`/admin/api-keys/${id}`);
    load();
  };
  const copy = (k) => {
    navigator.clipboard.writeText(k);
    toast.success("Clé copiée");
  };

  return (
    <div data-testid="admin-apikeys">
      <p className="text-sm text-brand-gray font-roboto mb-4">Clés API pour vos automatisations n8n. Conservez-les en lieu sûr.</p>
      <form onSubmit={create} className="flex gap-2 mb-6">
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="apikey-name-input" placeholder="Nom (ex: n8n-prod)" className="flex-1 h-10 px-3 bg-brand-navy/40 border border-brand-blue/60 rounded-sm text-white text-sm" />
        <button type="submit" data-testid="apikey-create-button" className="h-10 px-4 rounded-sm bg-brand-green text-black font-bold text-sm inline-flex items-center gap-2"><Key className="h-4 w-4" /> Générer</button>
      </form>
      {loading ? <Loader2 className="h-6 w-6 animate-spin text-brand-green" /> : keys.length === 0 ? (
        <p className="text-brand-gray font-roboto">Aucune clé.</p>
      ) : (
        <div className="space-y-2">
          {keys.map((k) => (
            <div key={k.id} className="border border-brand-blue/40 rounded-sm p-4 flex items-center gap-3" data-testid={`apikey-${k.id}`}>
              <Key className="h-4 w-4 text-brand-green flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-white text-sm">{k.name}</div>
                <div className="text-xs text-brand-gray/70 font-mono truncate">{k.key}</div>
              </div>
              <button onClick={() => copy(k.key)} className="p-2 text-white/70 hover:text-white" data-testid={`apikey-copy-${k.id}`}><Copy className="h-4 w-4" /></button>
              <button onClick={() => remove(k.id)} className="p-2 text-white/70 hover:text-red-400" data-testid={`apikey-delete-${k.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// silence unused
RefreshCw;
